"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { Input } from "@/components/ui/Input";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Button } from "@/components/ui/button";
import { getRoleHomePath } from "@/features/layout/role-paths";

const loginFormSchema = z.object({
  email: z.email(),
  password: z.string().min(6),
});

type LoginFormData = z.infer<typeof loginFormSchema>;

export default function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, signOut, userAccount } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [justSignedIn, setJustSignedIn] = useState(false);
  const showRegisteredMessage = searchParams.get("registered") === "true";
  const redirectPath = searchParams.get("redirect");

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    if (userAccount && justSignedIn) {
      router.push(redirectPath ?? getRoleHomePath(userAccount.role));
    }
  }, [redirectPath, userAccount, justSignedIn, router]);

  const onSubmit = async (data: LoginFormData) => {
    setError("");
    setLoading(true);

    try {
      await signIn(data.email, data.password);
      setJustSignedIn(true);
    } catch (err: any) {
      if (err.code === "auth/invalid-credential") {
        setError("Invalid email or password");
      } else if (err.code === "auth/user-not-found") {
        setError("No account found with this email");
      } else if (err.code === "auth/wrong-password") {
        setError("Incorrect password");
      } else {
        setError(err.message || "Failed to sign in");
      }
    } finally {
      setLoading(false);
    }
  };

  if (userAccount && !justSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 text-center">
          <h2 className="text-3xl font-extrabold">You&apos;re already signed in</h2>
          <p className="text-sm text-muted-foreground">
            Signed in as <span className="font-medium">{userAccount.email}</span>{" "}
            ({userAccount.role.replace(/_/g, " ")}).
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              onClick={() =>
                router.push(redirectPath ?? getRoleHomePath(userAccount.role))
              }
            >
              Continue
            </Button>
            <Button variant="outline" onClick={() => signOut()}>
              Sign out &amp; use a different account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center  py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold ">
            Sign in to Efasol
          </h2>
          <p className="mt-2 text-center text-sm">
            Your Complete Agricultural Platform
          </p>
        </div>

        <Form {...loginForm}>
          <form
            className="mt-8 space-y-6"
            onSubmit={loginForm.handleSubmit(onSubmit)}
          >
            {showRegisteredMessage && (
              <div className="rounded bg-green-50 p-4">
                <p className="text-sm text-green-800">
                  Registration successful! Please sign in.
                </p>
              </div>
            )}

            {error && (
              <div className="rounded bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="rounded shadow-xs -space-y-px">
              <FormField
                control={loginForm.control}
                name="email"
                render={({ field }) => {
                  return (
                    <FormItem className="mb-4">
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage></FormMessage>
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={loginForm.control}
                name="password"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <PasswordInput {...field} />
                      </FormControl>
                      <FormMessage></FormMessage>
                    </FormItem>
                  );
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link href="/forgot-password" className="font-medium">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4  text-sm font-medium rounded disabled:cursor-not-allowed"
              >
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </div>

            <div className="text-center">
              <p className="text-sm">
                Don&apos;t have an account?{" "}
                <Link href="/register" className="font-medium">
                  Register as a farmer
                </Link>
                <span className="mx-2 text-muted-foreground">|</span>
                <Link href="/register/customer" className="font-medium">
                  Register as a customer
                </Link>
              </p>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
